/**
 * Instance detail editor — setup, pack, version Try/Switch (REQ-AIS-015).
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  InstanceNotRunnableError,
  InstanceSetupRequiredError,
  resolveInstanceEntry,
} from '@/components/ai-studio/instances/resolveInstanceEntry';
import {
  createRun,
  getInstance,
  listIndustryPacks,
  listInstanceUpgrades,
  switchInstanceVersion,
  updateInstance,
} from '@/services/aiStudioAgentService';
import {
  getInstanceSetupStatus,
  type InstanceSetupStatusDto,
} from '@/services/aiStudioInstanceSetupService';
import type {
  AiStudioAgentInstance,
  AiStudioAgentVersion,
  AiStudioIndustryPack,
  StudioBindingStatus,
  UpdateInstanceRequest,
} from '@/types/aiStudioAgent';

export type InstanceJsonField =
  | 'instanceSetup'
  | 'autonomyFencesOverride'
  | 'toolConnections'
  | 'memoryScope';

interface InstanceDraft {
  instanceName: string;
  industryPackId: string;
  status: StudioBindingStatus;
  instanceSetup: Record<string, unknown>;
  autonomyFencesOverride: Record<string, unknown>;
  toolConnections: Record<string, unknown>;
  memoryScope: Record<string, unknown>;
}

function toDraft(instance: AiStudioAgentInstance): InstanceDraft {
  return {
    instanceName: instance.instanceName,
    industryPackId: instance.industryPackId ?? '',
    status: instance.status,
    instanceSetup: instance.instanceSetup,
    autonomyFencesOverride: instance.autonomyFencesOverride,
    toolConnections: instance.toolConnections,
    memoryScope: instance.memoryScope,
  };
}

function toRequest(draft: InstanceDraft): UpdateInstanceRequest {
  return {
    instanceName: draft.instanceName.trim(),
    industryPackId: draft.industryPackId || null,
    status: draft.status,
    instanceSetup: draft.instanceSetup,
    autonomyFencesOverride: draft.autonomyFencesOverride,
    toolConnections: draft.toolConnections,
    memoryScope: draft.memoryScope,
  };
}

export interface UseInstanceEditorResult {
  instance: AiStudioAgentInstance | null;
  draft: InstanceDraft | null;
  packs: AiStudioIndustryPack[];
  upgrades: AiStudioAgentVersion[];
  loading: boolean;
  saving: boolean;
  switching: boolean;
  tryingVersionId: string | null;
  dirty: boolean;
  jsonValid: boolean;
  errorKey: string | null;
  statusMessageKey: string | null;
  statusMessageParams: Record<string, string | number> | null;
  setField: <K extends keyof InstanceDraft>(field: K, value: InstanceDraft[K]) => void;
  setJsonValidity: (field: InstanceJsonField, valid: boolean) => void;
  save: () => Promise<void>;
  tryVersion: (version: AiStudioAgentVersion) => Promise<void>;
  switchVersion: (version: AiStudioAgentVersion) => Promise<void>;
  runInstance: () => Promise<void>;
  setupStatus: InstanceSetupStatusDto | null;
}

export function useInstanceEditor(instanceId: string | undefined): UseInstanceEditorResult {
  const navigate = useNavigate();
  const [instance, setInstance] = useState<AiStudioAgentInstance | null>(null);
  const [draft, setDraft] = useState<InstanceDraft | null>(null);
  const [baseline, setBaseline] = useState<InstanceDraft | null>(null);
  const [packs, setPacks] = useState<AiStudioIndustryPack[]>([]);
  const [upgrades, setUpgrades] = useState<AiStudioAgentVersion[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [switching, setSwitching] = useState(false);
  const [tryingVersionId, setTryingVersionId] = useState<string | null>(null);
  const [errorKey, setErrorKey] = useState<string | null>(null);
  const [statusMessageKey, setStatusMessageKey] = useState<string | null>(null);
  const [statusMessageParams, setStatusMessageParams] = useState<Record<
    string,
    string | number
  > | null>(null);
  const [invalidJsonFields, setInvalidJsonFields] = useState<string[]>([]);
  const [setupStatus, setSetupStatus] = useState<InstanceSetupStatusDto | null>(null);

  const refreshUpgrades = useCallback(async (id: string) => {
    try {
      setUpgrades(await listInstanceUpgrades(id));
    } catch {
      setUpgrades([]);
    }
  }, []);

  useEffect(() => {
    if (!instanceId) {
      setLoading(false);
      setErrorKey('aiStudio.instances.notFound');
      return;
    }

    let cancelled = false;
    setLoading(true);
    setErrorKey(null);
    setInvalidJsonFields([]);
    setStatusMessageKey(null);

    Promise.all([
      getInstance(instanceId),
      listIndustryPacks(),
      listInstanceUpgrades(instanceId),
      getInstanceSetupStatus(instanceId).catch(() => null),
    ])
      .then(([loaded, packRows, upgradeRows, setup]) => {
        if (cancelled) return;
        setInstance(loaded);
        setSetupStatus(setup);
        const next = toDraft(loaded);
        setDraft(next);
        setBaseline(next);
        setUpgrades(upgradeRows);
        setPacks(
          packRows.filter(
            (pack) =>
              pack.lifecycleStatus === 'published' || pack.id === loaded.industryPackId
          )
        );
      })
      .catch(() => {
        if (!cancelled) setErrorKey('aiStudio.instances.loadFailed');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [instanceId]);

  const dirty = useMemo(() => {
    if (!draft || !baseline) return false;
    return JSON.stringify(draft) !== JSON.stringify(baseline);
  }, [baseline, draft]);

  const setField = useCallback(<K extends keyof InstanceDraft>(field: K, value: InstanceDraft[K]) => {
    setDraft((current) => (current ? { ...current, [field]: value } : current));
  }, []);

  const setJsonValidity = useCallback((field: InstanceJsonField, valid: boolean) => {
    setInvalidJsonFields((current) => {
      if (valid) return current.filter((item) => item !== field);
      return current.includes(field) ? current : [...current, field];
    });
  }, []);

  const save = useCallback(async () => {
    if (!instanceId || !draft) return;
    if (!draft.instanceName.trim()) {
      setErrorKey('aiStudio.instances.nameRequired');
      return;
    }
    setSaving(true);
    setErrorKey(null);
    try {
      await updateInstance(instanceId, toRequest(draft));
      const refreshed = await getInstance(instanceId);
      setInstance(refreshed);
      const next = toDraft(refreshed);
      setDraft(next);
      setBaseline(next);
      await refreshUpgrades(instanceId);
    } catch {
      setErrorKey('aiStudio.instances.saveFailed');
    } finally {
      setSaving(false);
    }
  }, [draft, instanceId, refreshUpgrades]);

  const tryVersion = useCallback(
    async (version: AiStudioAgentVersion) => {
      if (!instanceId || !instance) return;
      if (instance.status !== 'active') {
        setErrorKey(
          instance.status === 'archived'
            ? 'aiStudio.instances.notRunnableArchived'
            : 'aiStudio.instances.notRunnableInactive'
        );
        return;
      }
      setTryingVersionId(version.id);
      setErrorKey(null);
      setStatusMessageKey(null);
      try {
        const run = await createRun({
          instanceId,
          runName: `Trial v${version.versionNumber} — ${instance.instanceName}`,
          trialAgentVersionId: version.id,
        });
        setStatusMessageKey('aiStudio.instances.trialStarted');
        setStatusMessageParams({ number: version.versionNumber });
        const params = new URLSearchParams({ instanceId });
        navigate(
          `/ai-studio/runs/${encodeURIComponent(run.id)}?${params.toString()}`
        );
      } catch {
        setErrorKey('aiStudio.instances.saveFailed');
      } finally {
        setTryingVersionId(null);
      }
    },
    [instance, instanceId, navigate]
  );

  const switchVersion = useCallback(
    async (version: AiStudioAgentVersion) => {
      if (!instanceId) return;
      const confirmed = window.confirm(
        `Switch production version pin to v${version.versionNumber}? Existing runs keep their original version.`
      );
      if (!confirmed) return;

      setSwitching(true);
      setErrorKey(null);
      try {
        const refreshed = await switchInstanceVersion(instanceId, version.id);
        setInstance(refreshed);
        const next = toDraft(refreshed);
        setDraft(next);
        setBaseline(next);
        await refreshUpgrades(instanceId);
      } catch {
        setErrorKey('aiStudio.instances.saveFailed');
      } finally {
        setSwitching(false);
      }
    },
    [instanceId, refreshUpgrades]
  );

  const runInstance = useCallback(async () => {
    if (!instanceId) return;
    setErrorKey(null);
    try {
      const { runId, conversationId } = await resolveInstanceEntry(instanceId);
      const params = new URLSearchParams({
        conversationId,
        instanceId,
      });
      navigate(`/ai-studio/runs/${encodeURIComponent(runId)}?${params.toString()}`);
    } catch (error: unknown) {
      if (error instanceof InstanceSetupRequiredError) {
        navigate(`/ai-studio/instances/${encodeURIComponent(instanceId)}/provision`);
        return;
      }
      if (error instanceof InstanceNotRunnableError) {
        setErrorKey(
          error.reason === 'inactive'
            ? 'aiStudio.instances.notRunnableInactive'
            : error.reason === 'archived'
              ? 'aiStudio.instances.notRunnableArchived'
              : 'aiStudio.instances.setupRequired'
        );
        return;
      }
      setErrorKey('aiStudio.instances.saveFailed');
    }
  }, [instanceId, navigate]);

  return {
    instance,
    draft,
    packs,
    upgrades,
    loading,
    saving,
    switching,
    tryingVersionId,
    dirty,
    jsonValid: invalidJsonFields.length === 0,
    errorKey,
    statusMessageKey,
    statusMessageParams,
    setField,
    setJsonValidity,
    save,
    tryVersion,
    switchVersion,
    runInstance,
    setupStatus,
  };
}
